import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

export default function YourMindzeraMap() {
  const navigate = useNavigate();
  const [mode, setMode]               = useState(null); // 'login' or 'new'
  const [accountType, setAccountType] = useState(null); // 'individual' or 'organization'
  const [seatCount, setSeatCount]     = useState(5);

  // --- dynamic plan data ---
  const [plans, setPlans]     = useState([]);
  const [orgPlan, setOrgPlan] = useState(null);

  // 1) Load all plans once
  useEffect(() => {
    fetch("https://mindzera-backend.onrender.com/api/plans")
      .then(res => res.json())
      .then(setPlans)
      .catch(err => console.error("Error loading plans:", err));
  }, []);

  // 2) When seatCount or accountType changes, fetch matching org plan
  useEffect(() => {
    if (accountType === "organization") {
      fetch(`https://mindzera-backend.onrender.com/api/plans/org/${seatCount}`)
        .then(res => {
          if (!res.ok) throw new Error(`Status ${res.status}`);
          return res.json();
        })
        .then(setOrgPlan)
        .catch(err => {
          console.error("Error loading org plan:", err);
          setOrgPlan(null);
        });
    }
  }, [seatCount, accountType]);

  // Helper for the individual monthly plan
  const monthlyPlan = plans.find(
    p => p.min_seats == null && p.name.toLowerCase().includes("monthly")
  );

  // Subscription handler
  // TODO: replace with actual logged-in user ID
  const userId = "<YOUR_USER_ID_HERE>";

  const handleSubscribe = async () => {
    const planId = accountType === "organization"
      ? orgPlan?.id
      : monthlyPlan?.id;

    if (!userId || !planId) {
      alert("Missing user or plan. Make sure you're logged in.");
      return;
    }

    try {
      const res = await fetch(
        "https://mindzera-backend.onrender.com/api/subscriptions",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: userId,
            plan_id: planId,
            seats: accountType === "organization" ? seatCount : null
          })
        }
      );

      if (!res.ok) throw new Error(`Status ${res.status}`);
      const data = await res.json();
      console.log("Subscription created:", data);
      navigate("/dashboard");
    } catch (err) {
      console.error("Subscription error:", err);
      alert("Subscription failed. Check console for details.");
    }
  };

  return (
    <main className="max-w-2xl mx-auto px-6 py-12 text-gray-800 text-left space-y-8">
      <h1 className="text-4xl font-extrabold text-center mb-6">Your Mindzera Map</h1>

      {/* Top-Level Choice */}
      {!mode && (
        <div className="flex flex-col md:flex-row justify-center gap-4">
          <button
            className="bg-gray-800 text-white px-6 py-3 rounded-xl hover:bg-gray-900 transition"
            onClick={() => setMode("login")}
          >
            I have a Mindzera account
          </button>
          <button
            className="bg-green-700 text-white px-6 py-3 rounded-xl hover:bg-green-800 transition"
            onClick={() => setMode("new")}
          >
            I’m new to Mindzera
          </button>
        </div>
      )}

      {/* Login Form */}
      <AnimatePresence>
        {mode === "login" && (
          <motion.div
            key="login"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="space-y-4"
          >
            <p className="text-lg font-medium">
              Sign in to continue building your Mindzera Map.
            </p>
            <input type="email" placeholder="Email" className="w-full border px-4 py-2 rounded-md" />
            <input type="password" placeholder="Password" className="w-full border px-4 py-2 rounded-md" />
            <button className="bg-gray-800 text-white px-6 py-2 rounded-xl hover:bg-gray-900 transition">
              Sign In
            </button>
            <p className="text-sm text-gray-600">
              Don’t have an account?{" "}
              <button onClick={() => setMode("new")} className="text-green-700 underline">
                Create one here
              </button>
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* New Account Type Choice */}
      <AnimatePresence>
        {mode === "new" && !accountType && (
          <motion.div
            key="chooseType"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="space-y-4"
          >
            <p className="text-lg font-medium">
              Are you signing up as an individual or an organization?
            </p>
            <div className="flex flex-col md:flex-row gap-4">
              <button
                className="bg-blue-700 text-white px-6 py-3 rounded-xl hover:bg-blue-800 transition"
                onClick={() => setAccountType("individual")}
              >
                I'm an individual
              </button>
              <button
                className="bg-green-700 text-white px-6 py-3 rounded-xl hover:bg-green-800 transition"
                onClick={() => setAccountType("organization")}
              >
                Organization (5 or more people)
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Individual Sign-Up */}
      <AnimatePresence>
        {accountType === "individual" && (
          <motion.div
            key="individual"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="space-y-4"
          >
            <p className="text-lg font-medium">Create your Mindzera account.</p>
            <p className="text-sm text-gray-600">
              {monthlyPlan
                ? `$${(monthlyPlan.price_cents / 100).toFixed(2)}/month`
                : "Loading..."}{" "}
              · 7-day Money-Back Guarantee
            </p>
            <input type="email" placeholder="Email" className="w-full border px-4 py-2 rounded-md" />
            <input type="password" placeholder="Password" className="w-full border px-4 py-2 rounded-md" />
            <button
              onClick={handleSubscribe}
              className="bg-blue-700 text-white px-6 py-2 rounded-xl hover:bg-blue-800 transition"
            >
              Create Account
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Organization Sign-Up */}
      <AnimatePresence>
        {accountType === "organization" && (
          <motion.div
            key="organization"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="space-y-4"
          >
            <p className="text-lg font-medium">Set up your organization’s Mindzera workspace.</p>
            <p className="text-sm text-gray-600">Minimum 5 seats required · 7-day Money-Back Guarantee</p>

            {/* Seat input */}
            <label className="block">
              Number of Seats (min 5)
              <input
                type="number"
                min={5}
                value={seatCount}
                onChange={e => setSeatCount(Math.max(5, parseInt(e.target.value, 10)))}
                className="w-full border px-4 py-2 rounded-md"
              />
            </label>

            {/* Dynamic tier info */}
            {orgPlan ? (
              <p className="text-sm">
                Plan: <strong>{orgPlan.name}</strong> —{" "}
                <strong>${(orgPlan.price_cents / 100).toFixed(2)} per seat</strong>
              </p>
            ) : (
              <p className="text-sm text-red-600">No tier available for that many seats</p>
            )}

            {/* Total cost (all org plans are monthly) */}
            {orgPlan && (
              <p className="text-sm text-gray-600">
                Total:{" "}
                <strong>${((orgPlan.price_cents / 100) * seatCount).toFixed(2)}/month</strong>
              </p>
            )}

            <button
              onClick={handleSubscribe}
              className="bg-green-700 text-white px-6 py-2 rounded-xl hover:bg-green-800 transition"
            >
              Create Organization Account
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer Note */}
      <div className="pt-8 text-sm text-center text-gray-500">
        By creating an account, you agree to our Terms of Service and Privacy Policy.
      </div>
    </main>
  );
}
